import React from 'react';
import {
  Container,
  Typography,
  Box,
  useTheme,
  Button,
} from '@mui/material';
import { EmojiEvents } from '@mui/icons-material';
import { ContentUseCase } from '../../corelogic/usecases/ContentUseCase';
import { useLanguage } from '../../shared/contexts/LanguageContext';
import { SEO } from './SEO';
import { BackToTop } from './BackToTop';

const contentUseCase = new ContentUseCase();

export const AboutUsPage: React.FC = () => {
  const theme = useTheme();
  const { language } = useLanguage();
  const { aboutUs, fundraiserInfo, mitmachen } = contentUseCase.getAllContent(language);

  const teamMembers = (aboutUs as any)?.team?.members ?? [];

  return (
    <Container maxWidth="xl" sx={{ py: theme.spacing(6) }}>
      <SEO page="about" />
      <Box sx={{ textAlign: 'center' }}>
        <Typography
          variant="h2"
          component="h1"
          gutterBottom
          sx={{
            mb: theme.spacing(6),
            display: 'inline-flex',
            alignItems: 'center',
            gap: 2,
          }}
        >
          {aboutUs.title}
          <Box
            component="img"
            src="/Light_blue_ribbon.svg.png"
            alt={language === 'de' ? 'Hellblaues Bewusstseinsband' : 'Light Blue Awareness Ribbon'}
            sx={{
              height: { xs: 40, md: 56 },
              width: 'auto',
            }}
          />
        </Typography>
      </Box>

      {/* Mission & Vision */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
          gap: theme.spacing(4),
          mb: theme.spacing(6),
          mx: 'auto',
          maxWidth: { xs: '100%', md: 1240 },
        }}
      >
        <Box
          sx={{
            borderRadius: theme.spacing(2),
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
            boxShadow: theme.shadows[1],
            p: theme.spacing(3),
          }}
        >
          <Typography
            variant="h5"
            component="h2"
            sx={{ fontWeight: 800, mb: theme.spacing(1), color: 'primary.main' }}
          >
            {language === 'de' ? 'Mission' : 'Mission'}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ maxWidth: { xs: '100%', md: 520 }, mx: 'auto' }}>
            {fundraiserInfo.description_short}
          </Typography>
        </Box>

        <Box
          sx={{
            borderRadius: theme.spacing(2),
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
            boxShadow: theme.shadows[1],
            p: theme.spacing(3),
          }}
        >
          <Typography
            variant="h5"
            component="h2"
            sx={{ fontWeight: 800, mb: theme.spacing(1), color: 'primary.main' }}
          >
            {language === 'de' ? 'Vision' : 'Vision'}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: theme.spacing(2), maxWidth: { xs: '100%', md: 520 }, mx: 'auto' }}>
            {fundraiserInfo.vision}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ mb: theme.spacing(8) }}>
        <Typography variant="h3" component="h2" gutterBottom align="center" sx={{ mb: theme.spacing(4) }}>
          {aboutUs.team.title}
        </Typography>

        {/* Team members */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
            gap: theme.spacing(4),
            mb: theme.spacing(4),
          }}
        >
          {teamMembers.map((member: any) => (
            <Box
              key={member.name}
              sx={{
                textAlign: 'center',
                borderRadius: theme.spacing(2),
                overflow: 'hidden',
                bgcolor: 'background.paper',
                boxShadow: theme.shadows[4],
              }}
            >
              <Box sx={{ width: '100%', aspectRatio: '4 / 3', position: 'relative' }}>
                {/*
                  Valentina & Lici photos use slightly different crop/scale so the cards look consistent.
                */}
                {(() => {
                  const isValentina = member.name === 'Valentina';
                  const isLici = member.name === 'Lici';

                  const objectPosition = isValentina
                    ? 'center 10%'
                    : isLici
                      ? 'center 30%'
                      : 'center';

                  // Do NOT scale down the image; scaling creates visible white corners.
                  // We rely on `objectPosition` to align the crop.
                  const transform = 'none';

                  return (
                    <Box
                      component="img"
                      src={member.imageSrc}
                      alt={member.imageAlt ?? member.name}
                      loading="lazy"
                      decoding="async"
                      onError={(e: any) => {
                        // Generic fallback if an image fails to load.
                        e.currentTarget.onerror = null;
                        e.currentTarget.src = '/team.png';
                      }}
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        objectPosition,
                        transform,
                        transformOrigin: 'center',
                      }}
                    />
                  );
                })()}
              </Box>

              <Box sx={{ p: theme.spacing(3) }}>
                <Typography variant="h6" component="h3" sx={{ fontWeight: 700, mb: 0.5 }}>
                  {member.name}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: theme.spacing(1) }}>
                  {member.role}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {member.description}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>

      </Box>

      <Box sx={{
        width: '100px',
        height: '2px',
        backgroundColor: 'primary.main',
        mx: 'auto',
        mb: theme.spacing(8),
      }} />

      <Box sx={{ textAlign: 'center' }}>
          <EmojiEvents sx={{ fontSize: theme.spacing(6), mb: theme.spacing(2), color: 'secondary.main' }} />
          <Typography variant="h3" component="h2" gutterBottom align="center" sx={{ mb: theme.spacing(4) }}>
            {aboutUs.joinOurCause.title}
          </Typography>
          <Typography variant="h6" color="text.secondary" paragraph align="center" sx={{ maxWidth: { xs: '100%', md: 1000 }, mx: 'auto' }}>
            {aboutUs.joinOurCause.description}
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph align="center" sx={{ maxWidth: { xs: '100%', md: 1000 }, mx: 'auto' }}>
            {aboutUs.joinOurCause.additionalInfo}
          </Typography>
      </Box>

      {/* Donation Call to Action */}
      <Box sx={{ textAlign: 'center', mb: theme.spacing(8) }}>
        <Button
          variant="contained"
          size="large"
          href={`mailto:${mitmachen.email.mailto}?subject=${encodeURIComponent(
            language === 'de' ? 'Kontakt - Run for M.E.' : 'Contact - Run for M.E.'
          )}`}
          sx={{
            px: theme.spacing(4),
            py: theme.spacing(1.5),
            fontSize: '1rem',
            fontWeight: 600,
            minWidth: 200,
          }}
        >
          {language === 'de' ? 'Kontakt aufnehmen' : 'Contact us'}
        </Button>
      </Box>

      <BackToTop />
    </Container>
  );
};
